import React, { useState } from 'react';
import { useUser } from '../hooks/useUser';
import { User, Mail, Pencil } from 'lucide-react';
import { errorMessage, updateProfile } from '../services/api';

const PSEUDONYM_MIN_LENGTH = 3;
const PSEUDONYM_MAX_LENGTH = 30;

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (!user) {
    return (
      <div className="text-center p-8">
        <p>Veuillez vous connecter pour voir votre profil.</p>
      </div>
    );
  }

  const startEditing = () => {
    setDraft(user.pseudonym);
    setError(null);
    setSaved(false);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setError(null);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const pseudonym = draft.trim();
    if (pseudonym === user.pseudonym) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await updateProfile({ pseudonym });
      setIsEditing(false);
      setSaved(true);
      refreshUser();
    } catch (err) {
      setError(errorMessage(err, 'Impossible de modifier le pseudonyme'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="glass dark:glass-dark rounded-xl p-8 max-w-2xl mx-auto relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
      <div className="relative z-10">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 border-b dark:border-gray-700 pb-4">
          Mon Profil
        </h1>
        <div className="space-y-4">
          <div className="flex items-start">
            <User className="w-5 h-5 mr-3 mt-0.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-500 dark:text-gray-400">Nom d'utilisateur</p>
              {isEditing ? (
                <form onSubmit={save} className="mt-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      maxLength={PSEUDONYM_MAX_LENGTH}
                      autoFocus
                      aria-label="Nouveau nom d'utilisateur"
                      aria-invalid={error ? true : undefined}
                      aria-describedby="pseudonym-help"
                      className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                    <button
                      type="submit"
                      disabled={isSaving || draft.trim().length < PSEUDONYM_MIN_LENGTH}
                      className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      disabled={isSaving}
                      className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                    >
                      Annuler
                    </button>
                  </div>
                  <p id="pseudonym-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Entre {PSEUDONYM_MIN_LENGTH} et {PSEUDONYM_MAX_LENGTH} caractères.
                  </p>
                  {error && (
                    <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {error}
                    </p>
                  )}
                </form>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-lg font-medium text-gray-800 dark:text-gray-200 break-all">{user.pseudonym}</p>
                  <button
                    type="button"
                    onClick={startEditing}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                  >
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </button>
                  {saved && (
                    <span role="status" className="text-sm text-green-600 dark:text-green-400">
                      Pseudonyme mis à jour
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center">
            <Mail className="w-5 h-5 mr-3 text-gray-500 dark:text-gray-400" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
              <p className="text-lg font-medium text-gray-800 dark:text-gray-200">{user.email}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
